import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api, type PageResponse } from '@/api/client'
import { Button } from '@/components/primitives'
import { Dialog, SelectField, TextField, CheckboxField, Notice, useApiErrorMessage } from '@/components/controls'
import { useT } from '@/i18n/context'
import { accountReachableFrom } from '@/lib/accountReach'
import type { ID, Provider } from '@/types/domain'

/** The account fields the start dialog needs — a subset of api.AccountOverview.
 *
 *  `hostId` is not decoration. A credential is host-local (ADR-0006): an account
 *  is signed in on one machine, and a session on another cannot reach it. The
 *  dialog used to omit it, so every account read as though it worked everywhere
 *  and the mismatch only surfaced as a refusal after Start was pressed. */
interface AccountLite {
  id: ID
  label: string
  provider: Provider
  enabled: boolean
  hostId?: ID
  hostName?: string
  /** Its credential directory is on storage the whole fleet mounts, so it works
   *  on every host (ADR-0013) and the host it was signed in on stops mattering. */
  sharedStorage?: boolean
}

/** The model fields the start dialog needs — a subset of api.ModelResponse. */
interface ModelLite {
  id: ID
  provider: Provider
  displayName: string
  externalId: string
  supportedEfforts?: string[]
  status: string
}

/** The project fields the start dialog needs, including its repositories so the
 *  dialog can show which server and directory the session will run in. */
interface RepoLite {
  id: ID
  name: string
  hostId: ID
  path: string
}
interface ProjectLite {
  id: ID
  name: string
  repositories?: RepoLite[]
}

/** The host fields the dialog needs to name a repository's server, and to know
 *  which one is the control host — the one machine where an account signed in
 *  anywhere is still usable, because that is the rule the server applies. */
interface HostLite {
  id: ID
  name: string
  isControlHost?: boolean
}

/** The session the server returns on a successful launch. */
interface StartedSession {
  id: ID
}

/**
 * StartSessionDialog is where an operator launches a CLI into a tmux session:
 * pick the project — its repository is the server and directory the session runs
 * in — an account, and optionally a model and effort. There is no free working
 * directory field: a session always runs somewhere a project already names, so
 * the working directory is the project's, not something typed here. On success it
 * attaches the new session so the pane opens straight away.
 */
export function StartSessionDialog({
  onClose,
  onStarted,
  initialProjectId,
  task,
}: {
  onClose: () => void
  onStarted: (id: ID) => void
  /** A project to seed the form with — a prefill, filled once and never
   *  overwriting what the operator picks. */
  initialProjectId?: ID
  /** The task this session is started for, when it is. The session records
   *  it and the task advances to RUNNING; the form names it so the operator
   *  knows what they are starting work on. The project is the task's. */
  task?: { id: ID; title: string; projectId: ID }
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const apiError = useApiErrorMessage()

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<PageResponse<AccountLite>>('/accounts'),
  })
  const models = useQuery({
    queryKey: ['models'],
    queryFn: () => api.get<PageResponse<ModelLite>>('/models'),
  })
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<PageResponse<ProjectLite>>('/projects'),
  })
  const hosts = useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<HostLite>>('/hosts'),
  })

  const enabledAccounts = useMemo(
    () => (accounts.data?.items ?? []).filter((a) => a.enabled),
    [accounts.data],
  )

  const [accountId, setAccountId] = useState<ID>('')
  const [modelId, setModelId] = useState<ID>('')
  const [effort, setEffort] = useState<string>('')
  const [projectId, setProjectId] = useState<ID>(task?.projectId ?? initialProjectId ?? '')
  const [name, setName] = useState<string>('')
  const [record, setRecord] = useState(false)
  const [continued, setContinued] = useState(false)
  const [resume, setResume] = useState(false)
  const [bypass, setBypass] = useState(false)

  // Project → account continuity: a project should keep running on the account
  // it last used. Selecting a project pre-fills the account, model and effort
  // from that project's most recent session — the association lives in this
  // history, not in a foreign key on the project — and the operator can still
  // change any of them before starting.
  const history = useQuery({
    queryKey: ['project-last-session', projectId],
    queryFn: () => api.get<PageResponse<StartedSession & { accountProfileId?: ID; modelId?: ID; effort?: string }>>(
      `/sessions?projectId=${projectId}`,
    ),
    enabled: projectId !== '',
  })
  // Once the operator has picked an account by hand, continuity must not
  // overwrite it. Ever — including when the project changes afterwards.
  //
  // That last word is the whole of a real defect. This flag used to be reset on
  // every project change, and the fields are laid out account-first,
  // project-fourth: filling the form top to bottom therefore *always* went
  // "operator picks an account" → "operator picks a project" → "the reset fires"
  // → "continuity silently replaces the account they just chose". Reported as
  // "I start Setget with koraykaradass@gmail.com and it always starts with
  // arpanetbilisim@gmail.com", and the session record agreed with the complaint:
  // the wrong account id left the browser.
  //
  // Silently substituting one credential for another is the worst thing this
  // dialog can do. It spends a different subscription's quota, writes into a
  // different conversation history, and does it without a word on screen. So an
  // explicit choice now outlives a project change, and continuity fills only a
  // field nobody has set.
  const touchedRef = useRef(false)

  useEffect(() => {
    if (touchedRef.current) {
      // Their choice stands, so this is not continuity and must not claim to be.
      setContinued(false)
      return
    }
    const last = history.data?.items?.[0]
    if (!last) {
      setContinued(false)
      return
    }
    if (last.accountProfileId && enabledAccounts.some((a) => a.id === last.accountProfileId)) {
      setAccountId(last.accountProfileId)
      setModelId(last.modelId ?? '')
      setEffort(last.effort ?? '')
      setContinued(true)
    }
  }, [history.data, enabledAccounts, projectId])

  // The account fixes the provider; only that provider's models are offerable,
  // because a session's model must belong to its account's provider.
  const account = enabledAccounts.find((a) => a.id === accountId)
  const providerModels = useMemo(
    () =>
      (models.data?.items ?? []).filter(
        (m) => account && m.provider === account.provider && m.status !== 'RETIRED',
      ),
    [models.data, account],
  )
  const model = providerModels.find((m) => m.id === modelId)
  const efforts = model?.supportedEfforts ?? []

  // The selected project's repositories are the session's destination: the server
  // it runs on and the directory it runs in. Shown read-only so the operator can
  // see where the work will land before starting it.
  const selectedProject = (projects.data?.items ?? []).find((p) => p.id === projectId)
  const repos = selectedProject?.repositories ?? []
  const hostName = (id: ID) => hosts.data?.items.find((h) => h.id === id)?.name ?? id
  const controlHostId = (hosts.data?.items ?? []).find((h) => h.isControlHost)?.id

  // Which repository the session will actually run in. This mirrors the server's
  // `chooseRepository`: the control host's if the project has one there,
  // otherwise the first with a path. The dialog used to list every repository
  // under "runs on", which reads as though all of them were involved — for a
  // project with checkouts on two machines it named the wrong one as often as
  // the right one, and the whole point of the line is to say where the work
  // will land.
  const target = useMemo(() => {
    const withPath = repos.filter((r) => r.path.trim() !== '')
    return withPath.find((r) => r.hostId === controlHostId) ?? withPath[0]
  }, [repos, controlHostId])

  // A credential lives on one machine. Unless the session runs on the control
  // host — where the server keeps its prior behaviour and accepts any profile —
  // the account has to be signed in on the very host the repository is on.
  // Checked here rather than left to the refusal, so the operator learns it when
  // they pick the project instead of when they press Start.
  //
  // Through the shared rule rather than spelled out again: this expression was
  // right and the switch dialog's was not, which is the argument for there being
  // one of them.
  const accountElsewhere =
    account != null &&
    target != null &&
    !accountReachableFrom(account, {
      id: target.hostId,
      isControlHost: target.hostId === controlHostId,
    })

  const start = useMutation({
    mutationFn: () =>
      api.post<StartedSession>('/sessions', {
        accountProfileId: accountId,
        modelId: modelId || undefined,
        effort: effort || undefined,
        projectId,
        name: name.trim() || undefined,
        record,
        resume,
        bypassApprovals: bypass,
        taskId: task?.id,
      }),
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
      onStarted(session.id)
    },
  })

  const canSubmit = accountId !== '' && projectId !== '' && !accountElsewhere && !start.isPending

  return (
    <Dialog
      title={t('live.startTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => start.mutate()}
            disabled={!canSubmit}
            busy={start.isPending}
          >
            {t('live.startSubmit')}
          </Button>
        </>
      }
    >
      {enabledAccounts.length === 0 ? (
        <Notice tone="warn">{t('live.startNoAccounts')}</Notice>
      ) : (
        <div className="k-form">
          {task && <Notice tone="info">{t('work.startForTask', { title: task.title })}</Notice>}
          <SelectField
            label={t('live.startAccount')}
            hint={continued ? t('live.continuedAccount') : undefined}
            value={accountId}
            onChange={(v) => {
              touchedRef.current = true
              setAccountId(v)
              setContinued(false)
              // The model belongs to the provider; changing account invalidates it.
              setModelId('')
              setEffort('')
            }}
            options={[
              { value: '', label: '—' },
              // The host is part of the account's identity here, not extra
              // detail: two accounts can be the same subscription signed in on
              // two machines, and without it the list offers what look like
              // duplicates of which only one can run the session.
              // A shared-storage account is named by its reach rather than by
              // one host, because naming a host would invite the reader to
              // conclude the very thing that is no longer true of it.
              ...enabledAccounts.map((a) => ({
                value: a.id,
                label: a.sharedStorage
                  ? `${a.label} · ${a.provider} · ${t('live.startEveryHost')}`
                  : `${a.label} · ${a.provider}${a.hostId ? ` · ${hostName(a.hostId)}` : ''}`,
              })),
            ]}
          />

          <SelectField
            label={t('live.startModel')}
            value={modelId}
            onChange={(v) => {
              setModelId(v)
              setEffort('')
            }}
            disabled={!account}
            options={[
              { value: '', label: t('live.startModelDefault') },
              ...providerModels.map((m) => ({ value: m.id, label: m.displayName })),
            ]}
          />

          {efforts.length > 0 && (
            <SelectField
              label={t('live.startEffort')}
              value={effort}
              onChange={setEffort}
              options={[
                { value: '', label: t('live.startEffortDefault') },
                ...efforts.map((e) => ({ value: e, label: e })),
              ]}
            />
          )}

          <SelectField
            label={t('live.startProject')}
            value={projectId}
            onChange={setProjectId}
            options={[
              { value: '', label: t('live.startProjectPick') },
              ...(projects.data?.items ?? []).map((p) => ({ value: p.id, label: p.name })),
            ]}
          />

          {selectedProject &&
            (target == null ? (
              <Notice tone="warn">{t('live.startProjectNoRepo')}</Notice>
            ) : (
              <Notice tone="info">
                {/* The account is named here, beside the host and the directory,
                    because this is the last moment before a credential is spent
                    and the field that chooses it is three rows further up. A
                    dialog that silently substituted one account for another is
                    exactly what this line exists to make impossible to miss. */}
                {t('live.startRunsOn')} {hostName(target.hostId)}{' '}
                <span className="k-mono">{target.path}</span>
                {account && (
                  <>
                    {' · '}
                    <span className="k-mono">{account.label}</span>
                  </>
                )}
                {/* Say that others exist rather than listing them as though
                    they were all destinations. Only one of them is. */}
                {repos.length > 1 && ` (${t('live.startOtherRepos', { n: String(repos.length - 1) })})`}
              </Notice>
            ))}

          {/* The refusal an operator used to meet only after pressing Start,
              moved to the moment it becomes true and given its remedy. */}
          {accountElsewhere && target && (
            <Notice tone="err">
              {t('live.startAccountElsewhere', {
                account: account?.label ?? '',
                on: account?.hostId ? hostName(account.hostId) : '—',
                target: hostName(target.hostId),
              })}
            </Notice>
          )}

          <TextField label={t('live.startName')} value={name} onChange={setName} />

          <CheckboxField label={t('live.startRecord')} checked={record} onChange={setRecord} />

          <CheckboxField
            label={t('live.startResume')}
            hint={t('live.startResumeHint')}
            checked={resume}
            onChange={setResume}
          />

          <CheckboxField
            label={t('live.startBypass')}
            hint={t('live.startBypassHint')}
            checked={bypass}
            onChange={setBypass}
          />
          {bypass && <Notice tone="warn">{t('live.startBypassWarn')}</Notice>}

          {start.error && <Notice tone="err">{apiError(start.error).title}</Notice>}
        </div>
      )}
    </Dialog>
  )
}
